export interface Brand {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  tenant_id: string;
  created_at: string;
  updated_at: string;
  // Spec 076: brand sharing / locks
  locked_by_email?: string | null;
  is_locked_by_me?: boolean;
  member_count?: number;
  // Spec 078: BCP-47 locale for discovery / DataForSEO lookups
  locale: string;
  // Phase 9 Session E.1: optional sitemap URL override. When null/unset the
  // backend falls back to `https://${domain}/sitemap.xml` for sitemap analyses.
  sitemap_url: string | null;
  // Spec 151 Phase A: optional markdown tone-of-voice guide. Used (Phase B) when
  // GEO Observatory suggests content changes. Null/unset when no guide has been added.
  tone_of_voice_guide?: string | null;
}

export interface User {
  email: string;
  name: string;
  picture?: string | null;
  /**
   * Spec 076: the server no longer returns a tenantId. Pages that need a
   * tenant id should use `activeTenantId` from BrandContext (settings/admin
   * flows) or `selectedBrand.tenant_id` (brand-scoped flows).
   */
  roles: string[];
  /**
   * Spec 088c: full 088a `/api/v1/me` shape. These fields enable
   * role-gated UI (ProtectedRoute, Sidebar group filtering).
   */
  isAdmin: boolean;
  isAppOwner: boolean;
  isDeveloper: boolean;
  appAccess: string[];
}

// Spec 076: brand members API responses
export interface BrandMember {
  email: string;
  added_by_email: string;
  added_at: string;
}

export interface BrandMembersResponse {
  data: {
    locked_by_email: string | null;
    members: BrandMember[];
  };
}

// Spec 191 — a brand's monitored regions (which regions it captures, + home).
export interface AvailableRegion {
  code: string;
  display_name: string;
}

export interface MonitoredRegion {
  code: string;
  display_name: string;
  is_home: boolean;
}

export interface BrandRegionsResponse {
  data: {
    available: AvailableRegion[];
    monitored: MonitoredRegion[];
  };
}

export interface BrandListResponse {
  data: Brand[];
  total: number;
  limit: number;
  offset: number;
}

export interface CitedDomain {
  domain: string;
  domain_type: string;
}

export interface ResponseListItem {
  id: string;
  engine: string;
  prompt_snippet: string;
  response_snippet: string;
  captured_at: string;
  token_usage: Record<string, number> | null;
  latency_ms: number | null;
  brand_mentioned: boolean | null;
  sentiment_label: string | null;
  mention_position: number | null;
  cited_domains: CitedDomain[] | null;
  // Spec 171: region dimension. Optional/tolerant of pre-region rows — the
  // region badge only renders when `region_code` is present.
  region_code?: string | null;
  region_mechanism?: RegionMechanism | null;
  region_fidelity?: RegionFidelity | null;
}

export interface ResponseListResponse {
  data: ResponseListItem[];
  total: number;
  limit: number;
  offset: number;
}

// ── Visibility Scores (Spec 018/022) ──

export type ReliabilityTier = 'directional' | 'operational' | 'high_confidence';

export interface ConfidenceInterval {
  lower: number;
  upper: number;
}

export interface VisibilityScore {
  engine: string;
  prompt_category: string;
  period: string;
  sample_size: number;
  mention_rate: number | null;
  avg_position: number | null;
  avg_sentiment: number | null;
  share_of_voice: number | null;
  quality_score: number | null;
  confidence_interval: Record<string, ConfidenceInterval> | null;
  computed_at: string;
  reliability: ReliabilityTier;
  // Unprompted metrics — measured only over queries where the brand
  // name/aliases do NOT appear in the rendered prompt template. Null when no
  // unprompted samples exist (every prompt named the brand).
  unprompted_sample_size?: number;
  unprompted_mentioned_count?: number;
  unprompted_mention_rate?: number | null;
  // Citation visibility — a SECOND, independent measurement alongside the
  // mention rate: was the brand's own domain cited as a source? Its denominator
  // is `grounded_sample_size` (responses where the engine actually searched the
  // web), NOT `sample_size` — an answer written from the model's weights alone
  // could not have cited anything, so folding it in would drag the rate toward
  // zero for a reason that has nothing to do with the brand.
  //
  // `brand_citation_rate === null` means NOT EVALUABLE (no web-searched answers
  // in this row). `0` means measured zero (searched answers exist, brand never
  // cited). These are different claims and must never be conflated.
  //
  // Optional so a payload from a pre-citation backend still type-checks.
  grounded_sample_size?: number;
  brand_retrieved_count?: number;
  brand_cited_count?: number;
  brand_retrieval_rate?: number | null;
  brand_citation_rate?: number | null;
  // The SAME citation measurement, restricted to the UNPROMPTED population
  // (queries whose rendered prompt did NOT name the brand). The Overview
  // headline is the unprompted mention rate whenever it exists, and a citation
  // number computed over a DIFFERENT population than the headline invites a
  // subtraction that isn't valid: branded prompts are exactly the ones most
  // likely to surface the brand's own domain, so an all-prompt citation rate
  // sitting beside an unprompted mention rate is biased upward. The card picks
  // whichever of the two populations matches the headline it is showing.
  //
  // Same null contract: `null` = not evaluable (no web-searched answers in the
  // unprompted population), `0` = measured zero.
  unprompted_grounded_sample_size?: number;
  unprompted_brand_cited_count?: number;
  unprompted_brand_citation_rate?: number | null;
}

// ── Region dimension (Spec 168–171) ──

/**
 * How a region was actually applied to an engine call. Mirrors the backend
 * `region_mechanism` enum. `none` = the engine cannot take a location, so the
 * region was not controlled at all.
 */
export type RegionMechanism =
  | 'api_location_param'
  | 'proxy_egress'
  | 'dataforseo_location'
  | 'prompt_stated'
  | 'none';

/**
 * How faithfully the region was reproduced. Mirrors the backend
 * `region_fidelity` enum. The honesty spine: `replicated` = region truly
 * applied, `approximated` = region hinted to the model, `indicative_only` =
 * region not controlled (the labeled gap → "partial coverage").
 */
export type RegionFidelity = 'replicated' | 'approximated' | 'indicative_only';

/**
 * Per-(region, engine) visibility score. A leaner shape than VisibilityScore —
 * the region read groups by region first, so position/sentiment/unprompted are
 * not carried here (the cards fall back to the raw mention rate).
 *
 * `region_mechanism`/`region_fidelity` are OPTIONAL at the engine level: when
 * the backend stamps honesty labels per (region, engine) they ride here and win;
 * otherwise the region-level labels apply to every engine in the region.
 */
export interface RegionEngineScore {
  engine: string;
  mention_rate: number;
  share_of_voice: number | null;
  sample_size: number;
  confidence_interval: ConfidenceInterval | null;
  reliability_tier: ReliabilityTier;
  region_mechanism?: RegionMechanism;
  region_fidelity?: RegionFidelity;
  /**
   * Citation visibility for this (region, engine) cell — derived from the cell's
   * OWN grounded n, never pooled across regions or engines (the same never-blend
   * invariant that keeps a US number out of a DK number).
   *
   * `brand_citation_rate === null` = not evaluable (no web-searched responses in
   * this cell); `0` = measured zero. The confidence interval and reliability tier
   * belong to the CITATION rate and are computed from `grounded_sample_size` —
   * they are not the mention rate's `confidence_interval`/`reliability_tier`.
   *
   * Required here: the region read is backend-computed in one place, so an absent
   * field would be a backend bug rather than an old payload.
   */
  grounded_sample_size: number;
  brand_retrieved_count: number;
  brand_cited_count: number;
  brand_retrieval_rate: number | null;
  brand_citation_rate: number | null;
  citation_confidence_interval: ConfidenceInterval | null;
  citation_reliability_tier: ReliabilityTier | null;
  /**
   * The same citation measurement over the UNPROMPTED population only, so a card
   * whose headline is the unprompted mention rate can show a citation number
   * computed over the same responses. Required for the same reason as the block
   * above: the region read is backend-computed in one place.
   */
  unprompted_grounded_sample_size: number;
  unprompted_brand_cited_count: number;
  unprompted_brand_citation_rate: number | null;
  unprompted_citation_confidence_interval: ConfidenceInterval | null;
  unprompted_citation_reliability_tier: ReliabilityTier | null;
}

/** One region's grouped scores. Single-region brands return exactly one of these. */
export interface RegionVisibility {
  region_code: string;
  display_name: string;
  is_home: boolean;
  /**
   * Spec 244 - NULLABLE, and always was.
   *
   * These were typed non-nullable, and the Overview crashed on the first brand
   * that had two monitored regions. The backend has never promised otherwise:
   * `regionRows` in `src/scoring/visibility.ts` types both columns
   * `string | null`, the dominant-label lookup defaults to
   * `{ region_mechanism: null, region_fidelity: null }` when a region has no
   * stamped labels, and migration `1782730000000_region_dimension_schema` adds
   * both columns WITHOUT `NOT NULL`. Null means "we never recorded how the
   * region was applied" - a real state for a region whose responses predate the
   * 168 honesty stamp, and the honest rendering of it is to say nothing.
   */
  region_mechanism: RegionMechanism | null;
  region_fidelity: RegionFidelity | null;
  engines: RegionEngineScore[];
}

export interface VisibilityResponse {
  data: VisibilityScore[];
  /**
   * Spec 171 (additive): per-region grouped scores. Present when the backend
   * has region data; single-region brands return one entry. When absent the SPA
   * falls back to the flat `data` shape — byte-identical to pre-171 behavior.
   */
  regions?: RegionVisibility[];
}

// Region source-overlap read (Spec 171)

/** Set-overlap of CITED domains between two regions. */
export interface RegionSourceOverlapPair {
  region_a: string;
  region_b: string;
  a_only: number;
  b_only: number;
  shared: number;
  overlap_pct: number;
  a_domains?: string[];
  b_domains?: string[];
}

export interface RegionSourceOverlapPerRegion {
  region_code: string;
  cited_domain_count: number;
}

export interface RegionSourceOverlapResponse {
  pairs: RegionSourceOverlapPair[];
  per_region: RegionSourceOverlapPerRegion[];
}

export interface VisibilityHistoryResponse {
  data: VisibilityScore[];
  granularity: string;
  date_from: string;
  date_to: string;
}

export interface CompetitorBrand {
  id: string;
  name: string;
  domain: string | null;
}

export interface CompetitorListResponse {
  data: CompetitorBrand[];
}

// ── Gaps (Spec 020, may not exist yet) ──

export type GapSeverity = 'critical' | 'high' | 'medium' | 'low';
/**
 * The gap types the detector actually emits (see the backend gap detector). The
 * six values below the divider are legacy/aspirational names that predate the
 * detector and are kept only so an older stored row still type-checks and still
 * gets a readable label instead of a raw slug.
 */
export type GapType =
  // Emitted by the detector today:
  | 'engine_gap'
  | 'sentiment_gap'
  | 'sov_decline'
  | 'position_gap'
  | 'category_gap'
  | 'competitor_domain_dominance'
  | 'retrieval_without_citation'
  | 'missing_authority_source'
  // Legacy names — never emitted, retained for old rows:
  | 'engine_absence'
  | 'negative_sentiment'
  | 'inaccurate_claims'
  | 'weak_citations'
  | 'source_gap';

/**
 * Why a lagging engine is lagging. Mirrors the backend classification.
 *
 * A mention-rate spread has always been read as a discoverability problem, but
 * an engine that synthesises from sources without naming them produces the same
 * spread for a completely different reason — and the discoverability play does
 * nothing about it. This says which of the two it is.
 *
 *  - `not_retrieved`       the brand's domain isn't reaching the engine at all.
 *  - `retrieved_not_cited` it reaches the engine's sources but is rarely credited.
 *  - `retrieved_not_named` it is retrieved AND cited, just not named in the prose.
 *  - `undetermined`        too few web-searched answers to tell. Never guessed.
 */
export type EngineGapPattern =
  | 'not_retrieved'
  | 'retrieved_not_cited'
  | 'retrieved_not_named'
  | 'undetermined';

/** What the backend attaches to an `engine_gap` in `supporting_data`. */
export interface EngineGapSupportingData {
  engines?: Record<string, number>;
  gap_magnitude?: number;
  sample_sizes?: Record<string, number>;
  pattern?: EngineGapPattern;
  citation_rates?: Record<string, number | null>;
  retrieval_rates?: Record<string, number | null>;
  grounded_sample_sizes?: Record<string, number>;
  /** The evidence the pattern label was computed from, so it can be checked. */
  pattern_basis?: {
    window_days: number;
    pooled_across: string[];
    lagging_engine: string;
    lagging_window_mention_rate: number | null;
    lagging_window_citation_rate: number | null;
    lagging_grounded_sample_size: number;
  };
}

export interface Gap {
  id: string;
  type: GapType;
  severity: GapSeverity;
  description: string;
  engine: string | null;
  prompt_category: string | null;
  /**
   * Structured evidence behind the gap. Optional: rows written before a given
   * detector version won't have every key, and the UI must degrade rather than
   * assume. For `engine_gap` this is shaped like `EngineGapSupportingData`.
   */
  supporting_data?: Record<string, unknown> | null;
}

export interface GapListResponse {
  data: Gap[];
}

// ── Tags (Spec 004/035) ──

export interface Tag {
  id: string;
  tenant_id: string;
  name: string;
  color: string;
  brand_count: number;
  created_at: string;
  updated_at: string;
}

export interface TagListResponse {
  data: Tag[];
}

// ── Tenant (Spec 003/035) ──

export interface TenantSettings {
  query_frequency?: number;
  default_engines?: string[];
  [key: string]: unknown;
}

export interface Tenant {
  id: string;
  name: string;
  plan: 'starter' | 'pro' | 'enterprise';
  settings: TenantSettings;
  created_at: string;
  updated_at: string;
}

export interface TenantResponse {
  data: Tenant;
}

// ── Brand with tags (extended for settings) ──

export interface BrandTag {
  id: string;
  name: string;
  color: string;
}

export interface BrandDetail extends Brand {
  aliases: string[] | null;
  description: string | null;
  tags: BrandTag[];
  competitor_count?: number;
}

export interface BrandDetailResponse {
  data: BrandDetail;
}

// ── Engine metadata ──

export const ENGINE_LABELS: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  google_aio: 'Google AIO',
  mistral: 'Mistral',
};

// ── Response Detail (Spec 024) ──

export interface ResponseSource {
  id: string;
  url: string;
  domain: string | null;
  domain_id: string | null;
  domain_type: string | null;
  domain_display_name: string | null;
  position: number;
  citation_type: string | null;
  is_retrieved: boolean;
  is_cited: boolean;
  snippet: string | null;
}

export interface CompetitorMention {
  brand_name: string;
  position: number | null;
  context: string | null;
  sentiment: string | null;
}

export interface KeyClaim {
  claim: string;
  accuracy: string | null;
}

export interface ResponseAnalysis {
  entity_extraction: {
    brand_mentioned: boolean;
    mention_position: number | null;
    mention_context: string | null;
    competitor_mentions: CompetitorMention[];
    analyzed_at: string | null;
  };
  classification: {
    mention_context: string | null;
    sentiment_score: number | null;
    sentiment_label: string | null;
    key_claims: KeyClaim[];
    relevance_score: number | null;
  };
  authority: {
    authority_score: number | null;
    brand_retrieval_rate: number;
    brand_citation_count: number;
    brand_cited_urls: string[];
  };
}

export interface ResponseDetail {
  id: string;
  raw_content: string;
  raw_content_hash: string | null;
  token_usage: Record<string, number> | null;
  latency_ms: number | null;
  captured_at: string;
  aio_sources: unknown[] | null;
  created_at: string;
  query_job: {
    id: string;
    engine: string;
    rendered_prompt: string;
    api_model: string | null;
    status: string;
    executed_at: string | null;
    brand_id: string;
    prompt_template_id: string | null;
    /**
     * Spec 094: BCP-47 locale (e.g. `'en-US'`, `'da-DK'`) from the linked
     * brand. Nullable when the response has no linked query_job/brand.
     * Used by the AIO renderer to badge snippets whose detected language
     * differs from the brand's primary locale.
     */
    brand_locale?: string | null;
  };
  analysis_status?: string;
  stage_statuses?: Record<string, string>;
  analysis: ResponseAnalysis | null;
  sources: ResponseSource[];
  /**
   * Capture provenance + web-search telemetry. All optional/tolerant of older
   * rows captured before these fields were recorded — render defensively.
   * Makes a "Sources (0)" response legible: a zero now displays its reason
   * (search not enabled vs invoked-but-cited-nothing vs extraction gap).
   */
  capture_mode?: CaptureMode;
  search_status?: SearchStatus;
  search_tool_attached?: boolean;
  search_invocations?: number;
  // Spec 160: NULL when the engine exposes no structured source channel
  // (Mistral/Gemini/google_aio surface citations in the response body) — distinct
  // from a real 0 (e.g. ChatGPT with search not enabled).
  sources_returned_by_engine?: number | null;
  sources_extracted_by_beacon?: number | null;
  // Spec 171: region dimension on the captured response. Optional/tolerant of
  // pre-region rows — the region badge only renders when `region_code` is set.
  region_code?: string | null;
  region_mechanism?: RegionMechanism | null;
  region_fidelity?: RegionFidelity | null;
}

/** How a response was captured. */
export type CaptureMode = 'api' | 'consumer_gui' | 'third_party_proxy';

/** Whether/how web search ran for a captured response. */
export type SearchStatus =
  | 'not_enabled'
  | 'enabled_not_invoked'
  | 'invoked'
  // Spec 199: a search ran but the engine produced no AI answer (for Google
  // AI Overview, a results page with no AI Overview block on it). Distinct
  // from 'invoked' (an answer that could cite sources) and from 'unknown'
  // (never recorded).
  | 'ran_no_answer'
  | 'unknown';

export interface ResponseDetailResponse {
  data: ResponseDetail;
}

// ── Domain Intelligence (Spec 026/027) ──

export interface DomainListItem {
  domain_id: string;
  domain: string;
  type: string;
  display_name: string | null;
  retrieval_rate: number | null;
  citation_rate: number | null;
  trend_retrieval: number | null;
  trend_citation: number | null;
  sample_size: number | null;
  engine: string | null;
  period: string | null;
  monthly_traffic: number | null;
}

export interface DomainListResponse {
  data: DomainListItem[];
  total: number;
  limit: number;
  offset: number;
  /** Spec 135 — brand's latest domain_scores.computed_at (ISO), null if none. */
  last_computed_at?: string | null;
}

export interface DomainUrlItem {
  url: string;
  url_type: string | null;
  domain_type: string | null;
  domain_name: string | null;
  mention_count: number;
  engine_count: number;
  engines: string[];
  last_cited: string | null;
  cited_count: number;
  retrieved_count: number;
  brand_mentioned: boolean | null;
  gsc_impressions_28d?: number;
}

export interface DomainUrlResponse {
  data: DomainUrlItem[];
  total: number;
  limit: number;
  offset: number;
}

// ── Playbook Types (Spec 030/032) ──

export interface PlaybookListItem {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  summary: string | null;
  error_message: string | null;
  generated_at: string | null;
  created_at: string;
  action_count: number;
}

export interface PlaybookListResponse {
  data: PlaybookListItem[];
  total: number;
  limit: number;
  offset: number;
}

// ── Impact (Spec 031/032) ──

export interface ImpactMeasurement {
  id: string;
  playbook_id: string;
  status: string;
  measured_at: string | null;
  baseline_scores: Record<string, unknown> | null;
  current_scores: Record<string, unknown> | null;
  delta: Record<string, unknown> | null;
  actions_completed: number | null;
  actions_total: number | null;
  statistical_significance: boolean | null;
  model_update_detected: boolean | null;
  model_update_warnings: string[] | null;
  attribution: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface ImpactListResponse {
  data: ImpactMeasurement[];
  total: number;
}

// ── Cross-engine Comparison (Spec 025) ──

export interface ComparisonResponse {
  id: string;
  engine: string;
  rendered_prompt: string;
  raw_content: string;
  captured_at: string;
  token_usage: Record<string, number> | null;
  latency_ms: number | null;
  api_model: string | null;
  brand_mentioned: boolean | null;
  sentiment_label: string | null;
  mention_position: number | null;
  mention_context: string | null;
}

export interface ComparisonData {
  data: ComparisonResponse[];
}

// ── Prompt Suggestion (Spec 033/034) ──

export interface PromptSuggestion {
  id: string;
  brand_id: string;
  source: string;
  suggested_prompt: string;
  category: string | null;
  search_volume: number | null;
  ai_search_volume: number | null;
  volume_trend: string | null;
  locale: string | null;
  rationale: string | null;
  status: string;
  converted_to: string | null;
  suggested_at: string;
}

export interface PromptSuggestionListResponse {
  data: PromptSuggestion[];
  total: number;
  limit: number;
  offset: number;
}

// ── Upstream Engine Degradation (Spec 134 Part B) ──

/**
 * Health snapshot of the AI engines feeding collection, attached by the
 * backend to the Collection-preview and Overview pipeline-status responses.
 * Drives the operator-facing degradation banner: warns when engines are
 * failing (bad key, rate-limit, outage, empty responses) or when the app is
 * serving mock/synthetic data instead of live results.
 */
export interface EngineDegradationDetail {
  engine: string; // 'chatgpt' | 'gemini' | 'claude' | 'google_aio' | 'mistral'
  failure_class: string; // 'auth_invalid_key'|'rate_limited'|'timeout'|'network'|'provider_5xx'|'empty_response'|'provider_unavailable'|'unknown'
  /**
   * Failed jobs in the window, or NULL when the row was sourced from the
   * provider kill switch rather than from job failures (spec 220). Null, not
   * 0: a disabled provider short-circuits before it can fail, so `0` would be
   * arithmetically true and read as "no problem".
   */
  count: number | null;
  last_error: string | null;
  last_error_at: string | null; // ISO
}

export interface EngineDegradation {
  degraded: boolean;
  window_hours: number;
  engines: EngineDegradationDetail[];
  /** Engines currently served by mock/synthetic adapters (no live key). */
  mock_engines: string[];
}

// ── Pipeline Status ──

export interface PipelineStatus {
  queries: {
    total: number;
    last_executed_at: string | null;
    by_engine: Record<string, number>;
    /** Spec 256: each engine's own latest completed query (ISO). Absent key = never ran. */
    last_by_engine?: Record<string, string | null>;
  };
  analysis: {
    total_analyzed: number;
    pending: number;
    last_analyzed_at: string | null;
  };
  visibility: {
    last_computed_at: string | null;
    score_count: number;
    is_stale: boolean;
  };
  gaps: {
    last_detected_at: string | null;
    gap_count: number;
    is_stale: boolean;
  };
  domain_scores: {
    last_computed_at: string | null;
    score_count: number;
    is_stale: boolean;
  };
  /** Spec 134 Part B — upstream engine health for the degradation banner. */
  degradation?: EngineDegradation | null;
}

// ── Dashboard (Spec 041) ──

export interface DashboardOverview {
  data: {
    tenant_id?: string;
    total_brands: number;
    total_queries_24h: number;
    total_responses: number;
    active_playbooks: number;
    total_gaps: number;
  };
}

export interface DashboardEngineItem {
  engine: string;
  total_queries: number;
  completed: number;
  failed: number;
  avg_latency_ms: number | null;
}

export interface DashboardEnginesResponse {
  data: DashboardEngineItem[];
}

export interface DashboardSourceItem {
  domain: string;
  type: string;
  display_name: string | null;
  citation_count: number;
  cited_count: number;
  retrieved_count: number;
}

export interface DashboardSourcesResponse {
  data: DashboardSourceItem[];
}

// ── Prompt Template (for active prompts tab) ──

export interface PromptTemplateMetrics {
  mention_rate: number | null;
  avg_sentiment: number | null;
  sample_count: number;
  response_count: number;
  last_run_at: string | null;
}

export interface PromptTemplate {
  id: string;
  tenant_id?: string;
  template: string;
  category: string;
  locale?: string | null;
  is_active: boolean;
  is_seeded: boolean;
  created_at: string;
  updated_at: string;
  metrics?: PromptTemplateMetrics;
}

export interface PromptTemplateListResponse {
  data: PromptTemplate[];
  total: number;
  limit: number;
  offset: number;
}

// ── Collection Cockpit ──

export interface CollectionPreview {
  brands: Array<{
    id: string;
    name: string;
    is_competitor: boolean;
    template_count?: number;
    /**
     * True when this brand's latest collection run is stuck: a running job past
     * the reclaim floor, or an eligible-now pending job over an hour old.
     * Scheduled-later jobs are NOT stalled. Optional so older responses don't break.
     */
    collection_stalled?: boolean;
    /** Region dimension — true when this brand has the flag on + ≥2 monitored regions. */
    multi_region_active?: boolean;
    /** Region codes this brand is monitored across, e.g. ["US","DK"]. */
    monitored_regions?: string[];
  }>;
  templates: Array<{ id: string; template_snippet: string; category: string }>;
  engines: string[];
  samples_per_combo: number;
  total_queries: number;
  estimated_cost: number;
  cost_breakdown: Record<string, number>;
  // Region dimension — true when ≥1 brand in scope is multi-region (flag on +
  // ≥2 monitored regions). When true, `per_region_queries` explains how the
  // `total_queries` count splits across regions. Optional/additive so single-
  // region responses and older clients render unchanged.
  region_capture_active?: boolean;
  per_region_queries?: Record<string, number>;
  // Spec 095: brand-level breakdown of pending + running for the three
  // KPI tiles. `jobs_running_recovered_24h` counts query_jobs whose
  // `last_error` matches the recovery markers from spec 095 and whose
  // `updated_at` is within the last 24 hours. Optional for backwards-
  // compat with older clients that haven't refreshed.
  jobs_eligible_now?: number;
  jobs_scheduled_later?: number;
  jobs_running?: number;
  jobs_running_recovered_24h?: number;
  /** Spec 134 Part B — upstream engine health for the degradation banner. */
  degradation?: EngineDegradation | null;
}

export interface CollectionPreviewResponse {
  data: CollectionPreview;
}

export interface CollectionMatrixEngine {
  last_run: string | null;
  sample_count: number;
  fail_count: number;
  pending_count: number;
  last_status: string | null;
  last_error: string | null;
}

export interface CollectionMatrixRow {
  prompt_template_id: string;
  template_snippet: string;
  category: string;
  // Cross-region aggregate — always present; used for the single-region /
  // default view.
  engines: Record<string, CollectionMatrixEngine>;
  // Region dimension — region codes present for this template (sorted), e.g.
  // ["DK","US"]. Empty/1-length for single-region templates.
  regions?: string[];
  // Per-region cells: regionCode -> engine -> stat. Same EngineStat shape as
  // `engines`. Present only when regions are tracked.
  by_region?: Record<string, Record<string, CollectionMatrixEngine>>;
}

export interface CollectionMatrixResponse {
  data: CollectionMatrixRow[];
}

export interface CollectionRun {
  triggered_at: string;
  jobs_created: number;
  jobs_completed: number;
  jobs_failed: number;
  jobs_pending: number;
  // Spec 095: additive three-way breakdown of pending + running per
  // 5-minute bucket. `jobs_pending` is preserved for backwards-compat
  // and always equals `jobs_eligible_now + jobs_scheduled_later`.
  jobs_eligible_now?: number;
  jobs_scheduled_later?: number;
  jobs_running?: number;
  total_cost: number;
  duration_ms: number | null;
  // Spec 172 (#5): terminal status of the run. Optional for backwards-compat
  // with older clients/rows; the UI falls back to deriving it from counts.
  run_status?: 'in_progress' | 'complete' | 'partial' | 'failed';
  // Region dimension — per-region job counts for this run, e.g.
  // { "US": 35, "DK": 35 }. Empty {} for single-region runs.
  per_region?: Record<string, number>;
}

export interface CollectionHistoryResponse {
  data: CollectionRun[];
  total: number;
}

export interface SchedulerStatus {
  running: boolean;
  lastRun: string | null;
  nextRun: string | null;
  lastRunStats: {
    jobsCreated: number;
    brandsProcessed: number;
    errors: number;
  } | null;
}

// Spec 079: brand keyword coverage via DataForSEO ranked_keywords
// Extended 2026-05-04: 'upload' (manual CSV stand-in), 'ahrefs' (primary
// source going forward — better depth on niche / non-English domains).
export type KeywordSource = 'dataforseo' | 'upload' | 'ahrefs';

export interface BrandKeyword {
  id: string;
  brand_id: string;
  keyword: string;
  search_volume: number;
  rank_position: number | null;
  ranking_url: string | null;
  search_intent: string | null;
  fetched_at: string;
  source?: KeywordSource;
}

export interface KeywordsListResponse {
  data: BrandKeyword[];
  total: number;
  last_fetched_at: string | null;
}

export interface KeywordsRefreshResponse {
  data: {
    total: number;
    fetched_at: string;
    cost_estimate_usd: number;
  };
}

export interface KeywordsUploadResponse {
  data: {
    total: number;
    skipped: number;
    fetched_at: string;
    source: 'upload';
    parse_warnings: string[];
  };
}

// Spec 080: llms.txt generation
export type LlmsTxtKind = 'llms_txt' | 'llms_full_txt' | 'served';

export interface BrandLlmsTxt {
  id: string;
  brand_id: string;
  version: number;
  kind: LlmsTxtKind;
  content: string;
  source_inputs: Record<string, unknown>;
  generator_model: string | null;
  token_cost_usd: number | null;
  generated_by_email: string | null;
  generated_at: string;
}

export interface LlmsTxtCoverage {
  level: 'full' | 'partial' | 'sparse';
  reason: string | null;
}

export interface LlmsTxtGenerateResponse {
  data: BrandLlmsTxt & { coverage: LlmsTxtCoverage };
}

export interface LlmsTxtListResponse {
  data: BrandLlmsTxt[];
}

export interface LlmsTxtDetailResponse {
  data: BrandLlmsTxt;
}

export interface LlmsTxtLatestResponse {
  data: BrandLlmsTxt | null;
}

export interface LlmsTxtFetchServedResponse {
  data:
    | { served: BrandLlmsTxt; diff: string }
    | { served: null; reason: string };
}
